import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import GlobusGrid from './GlobusGrid';
import GlobusCollections from './GlobusCollections';
import React, { useEffect } from 'react';
import {
    Button,
    Stack, Typography,
} from '@mui/material';
import { useGlobusAuth } from "@globus/react-auth-context";
import { ls, TGlobusError } from "../../hooks/useGlobusTransfer";
import { useNavigate } from "react-router-dom";
import GlobusErrorView from "./GlobusErrorView";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedStudy } from '../common/databaseSlice';
import GlobusTransferProgress from "./GlobusTransferProgress";
import LassoModal from "../../components/LassoModal";
import useLocalStorage from "../../hooks/useLocalStorage";
import { selectLoading, setSelectedQueryTool } from "../data-wrangler/dataWranglerSlice";
import {
    selectUserCollection,
    UserCollection,
    globusTransferFilesReset,
    selectGlobusTransferStatus
} from "../data-wrangler/globusSlice";
import { useUserInfoQuery } from "../../api/userApiSlice";
import { useBindGlobusIdToUserMutation } from "../../api/globusApiSlice";

const queryClient = new QueryClient()

const GlobusController = () => {
    const dispatch = useDispatch();
    const { data: userData } = useUserInfoQuery({});
    const [bindGlobusIdToUser] = useBindGlobusIdToUserMutation();
    const userCollection: UserCollection = useSelector(selectUserCollection);
    const { authorization, isAuthenticated } = useGlobusAuth();
    const navigate = useNavigate();
    const queryLoading = useSelector(selectLoading);
    const transferStatus = useSelector(selectGlobusTransferStatus);
    const [fileSelection] = useLocalStorage("globus-file-list", {});

    const selectedStudy = useSelector(selectSelectedStudy);

    const { isSuccess, isFetching, error, refetch: reload } = useQuery({
        enabled: isAuthenticated,
        queryKey: ["globus_collections", "ls", "/"],
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async () => ls(authorization, selectedStudy.toLowerCase().includes('pre-release') ? window.env.globusHBCD : window.env.globusABCD, '/', {}),
    });

    useEffect(() => {
        if (userData.globusId || !authorization?.user) return;
        const bindGlobusId = async () => {
            try {
                await bindGlobusIdToUser({
                    username: userData.username,
                    body: {
                        globus_id: authorization?.user?.sub,
                    }
                }).unwrap();
            } catch (error) {
                console.error("Error trying bind globus with user: ", error);
            }
        }
        bindGlobusId();
    }, [authorization, bindGlobusIdToUser, userData]);

    useEffect(() => {
        return () => {
            dispatch(globusTransferFilesReset());
        }
    }, []);

    useEffect(() => {
        if (Object.keys(fileSelection).length === 0) {
            dispatch(setSelectedQueryTool("allStudyData"));
            navigate("/data-wrangler");
        }
    }, [fileSelection]);

    const globusError = error as unknown as TGlobusError;

    if (isFetching || queryLoading) {
        return <div>loading</div>
    }

    const reauth = () => {
        authorization?.login({
            additionalParams: {
                session_message: "You must reauth with globus to access transfer collection",
                session_required_identities: authorization?.user.sub,
                prompt: 'login'
            }
        });
    }

    const render = () => {
        if (transferStatus.status) return <GlobusTransferProgress/>
        if (!isAuthenticated || !isSuccess || error) {
            return (
                <GlobusErrorView
                    isAuthenticated={isAuthenticated}
                    isSuccess={isSuccess}
                    globusError={globusError}
                    reload={reload}
                    login={async () => await authorization?.login({})} reauth={reauth}
                />
            )
        }
        if (!userCollection.id) {
            return <GlobusCollections reload={reload}/>
        }
        return <GlobusGrid/>
    };

    return (
        <QueryClientProvider client={queryClient}>
            <Button
                data-testid="button-back"
                variant="tertiary"
                onClick={() => navigate("/data-wrangler", { state: { event: "globusBack" } })}
            >
                Back
            </Button>
            <Stack sx={{ overflowY: 'scroll' }} gap={3} height={"calc(100% - 100px)"} mt={3}>
                {render()}
            </Stack>
            <LassoModal open={transferStatus.error} onClose={() => dispatch(globusTransferFilesReset())}>
                <Stack>
                    <Typography variant="h6" sx={{ mt: 2, textAlign: "center", fontWeight: 600 }}>
                        Error triggering transfer
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        {transferStatus.errorMessage}
                    </Typography>
                    <Button variant="primary" onClick={() => dispatch(globusTransferFilesReset())}
                        sx={{ alignSelf: "center", mt: 3 }}>Ok</Button>
                </Stack>
            </LassoModal>
        </QueryClientProvider>
    )
}

export default GlobusController;